/**
 * Cross-tab recording guard & logout protection using localStorage.
 *
 * ## How it works
 *
 * 1. When a recording starts, the recording component calls `setActiveRecording()`
 *    to write an entry to localStorage, then starts a **heartbeat** interval that
 *    calls `touchRecordingHeartbeat()` every 3 seconds.
 *
 * 2. Any other tab (or page) that wants to know if a recording is live calls
 *    `getActiveRecording()`. This returns the entry ONLY if the heartbeat is
 *    fresh (≤ HEARTBEAT_STALE_MS). A dead / refreshed / closed tab stops
 *    updating the heartbeat, so the entry becomes stale automatically.
 *
 * 3. On `beforeunload` (page refresh / close), the recording component calls
 *    `pauseRecordingHeartbeat()` to zero-out the heartbeat immediately, so the
 *    new page load (after refresh) won't see a "live" entry.
 *
 * 4. `isLogoutLocked()` is derived from `getActiveRecording()` — if a live
 *    recording exists, auto-logout is blocked. No separate lock key needed.
 *
 * 5. The recording component keeps a local ref (`amRecordingRef`) to know
 *    whether *this* component instance owns the recording. This avoids the
 *    need for any cross-tab ID (TAB_ID) logic.
 */

const RECORDING_KEY = "activeRecording";

/** A recording is considered dead if its heartbeat hasn't been updated in this long. */
const HEARTBEAT_STALE_MS = 10_000; // 10 seconds

/** Heartbeat update interval — must be well under HEARTBEAT_STALE_MS. */
export const HEARTBEAT_INTERVAL_MS = 3_000; // 3 seconds

export interface ActiveRecordingInfo {
  sessionId: string;
  patientGuid: string;
  userId: string;
  patientName: string;
  heartbeat: number; // Date.now() — updated every HEARTBEAT_INTERVAL_MS
}

// ---------------------------------------------------------------------------
//  Write helpers (called from the recording component)
// ---------------------------------------------------------------------------

/**
 * Register a new active recording. Called when recording starts or resumes.
 */
export function setActiveRecording(
  info: Omit<ActiveRecordingInfo, "heartbeat">
): void {
  const entry: ActiveRecordingInfo = { ...info, heartbeat: Date.now() };
  localStorage.setItem(RECORDING_KEY, JSON.stringify(entry));
}

/**
 * Refresh the heartbeat. Called every HEARTBEAT_INTERVAL_MS by the recording
 * component so other tabs can tell the recording is still alive.
 */
export function touchRecordingHeartbeat(): void {
  const raw = localStorage.getItem(RECORDING_KEY);
  if (!raw) return;
  try {
    const info: ActiveRecordingInfo = JSON.parse(raw);
    info.heartbeat = Date.now();
    localStorage.setItem(RECORDING_KEY, JSON.stringify(info));
  } catch {
    /* corrupt entry — leave it for getActiveRecording to clean up */
  }
}

/**
 * Zero-out the heartbeat so the entry is immediately considered stale.
 * Call in the `beforeunload` handler so that after a page refresh the newly
 * loaded page won't see a "live" recording from the old page.
 */
export function pauseRecordingHeartbeat(): void {
  const raw = localStorage.getItem(RECORDING_KEY);
  if (!raw) return;
  try {
    const info: ActiveRecordingInfo = JSON.parse(raw);
    info.heartbeat = 0;
    localStorage.setItem(RECORDING_KEY, JSON.stringify(info));
  } catch {
    /* ignore */
  }
}

/**
 * Remove the active recording entry entirely.
 * Call when recording ends (stop / cancel / finalize / unmount).
 */
export function clearActiveRecording(): void {
  localStorage.removeItem(RECORDING_KEY);
}

// ---------------------------------------------------------------------------
//  Read helpers (called from any tab / component)
// ---------------------------------------------------------------------------

/**
 * Get the current active recording info.
 * Returns `null` if there is no entry or if the heartbeat is stale.
 */
export function getActiveRecording(): ActiveRecordingInfo | null {
  const raw = localStorage.getItem(RECORDING_KEY);
  if (!raw) return null;
  try {
    const info: ActiveRecordingInfo = JSON.parse(raw);
    if (!info.heartbeat || Date.now() - info.heartbeat > HEARTBEAT_STALE_MS) {
      // Stale — recording tab is dead / refreshed / closed
      return null;
    }
    return info;
  } catch {
    localStorage.removeItem(RECORDING_KEY);
    return null;
  }
}

/**
 * Check whether auto-logout should be blocked.
 * Returns true when a recording is alive (heartbeat is fresh).
 */
export function isLogoutLocked(): boolean {
  return getActiveRecording() !== null;
}

/**
 * Check if a specific server session ID is currently being recorded live.
 * Used to filter the "interrupted recording" popup — prevents showing a
 * resume prompt for a session that is actually still being recorded.
 */
export function isSessionLive(sessionId: string): boolean {
  const info = getActiveRecording();
  return info !== null && info.sessionId === sessionId;
}

/**
 * Force-remove the recording entry regardless of heartbeat.
 * Call on explicit user-confirmed logout.
 */
export function forceCleanupAllLocks(): void {
  localStorage.removeItem(RECORDING_KEY);
}

/**
 * Clear the active recording entry if it belongs to the given session ID,
 * regardless of whether the heartbeat is stale or fresh.
 * Call when a pending session is explicitly dismissed/deleted so the entry
 * doesn't produce a false "recording active in other tab" block on this tab.
 */
export function clearActiveRecordingIfSession(sessionId: string): void {
  const raw = localStorage.getItem(RECORDING_KEY);
  if (!raw) return;
  try {
    const info: ActiveRecordingInfo = JSON.parse(raw);
    if (info.sessionId === sessionId) {
      localStorage.removeItem(RECORDING_KEY);
    }
  } catch {
    localStorage.removeItem(RECORDING_KEY);
  }
}
